import { motion } from "framer-motion";
import { ArrowUpRight, Linkedin } from "lucide-react";

// Update this to the exact LinkedIn newsletter URL for "The PMC Perspective".
// Fallback: LinkedIn profile articles feed.
const NEWSLETTER_URL =
  "https://www.linkedin.com/in/asad-hatimi-program-leader/recent-activity/articles/";
const LINKEDIN_ARTICLES_URL =
  "https://www.linkedin.com/in/asad-hatimi-program-leader/recent-activity/articles/";

// Editorial themes — represent the newsletter's editorial scope. Individual
// article links can be added later via LinkedIn as they publish.
const themes = [
  {
    tag: "Governance",
    title: "Reading the plan before the plan reads the board.",
    body:
      "Why upstream stage-gate discipline is the single largest predictor of board-level surprise — and how to instrument it.",
    href: "/insights/reading-the-plan-before-the-board/",
  },
  {
    tag: "CapEx",
    title: "The arithmetic of cost overruns, plainly stated.",
    body:
      "A field guide to the ten most common variance patterns on ₹1,000+ Cr capital projects — with the countermeasures that actually work.",
    href: "/insights/arithmetic-of-cost-overruns/",
  },
  {
    tag: "Risk",
    title: "Constraint management is not a Gantt chart.",
    body:
      "How Critical Chain thinking reframes schedule risk from a calendar exercise into a scarce-resource discipline.",
    href: "/insights/",
  },
  {
    tag: "Culture",
    title: "PSSR is not paperwork. It is the promise.",
    body:
      "Pre-startup safety review as a leadership behaviour — what boards should ask before, during and after commissioning.",
    href: "/insights/",
  },
];

const row = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Perspectives() {
  return (
    <section id="perspectives" className="relative border-b border-surface-2">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass">
              <span className="tabular">10 —</span><span className="ml-3">Practice Perspectives</span>
            </div>
            <h2 className="mt-4 font-serif-display font-light text-ivory text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              The PMC Perspective<span className="text-brass italic">.</span>
            </h2>
            <p className="mt-6 text-mutedfg text-base md:text-lg font-light leading-relaxed max-w-xl">
              A LinkedIn newsletter on program governance, CAPEX discipline and independent oversight —
              written for practitioners, chairs and audit committees.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <a
              href={NEWSLETTER_URL}
              data-testid="newsletter-subscribe-btn"
              className="group flex items-center justify-between gap-3 border border-brass px-6 py-5 text-brass hover:bg-brass hover:text-ink transition-colors duration-500"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] mb-1">Newsletter</div>
                <div className="font-serif-display text-lg italic">Read on LinkedIn</div>
              </div>
              <ArrowUpRight size={22} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href={LINKEDIN_ARTICLES_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="linkedin-articles-link"
              className="mt-4 inline-flex items-center gap-2 text-mutedfg hover:text-brass link-underline text-[11px] uppercase tracking-[0.24em]"
            >
              <Linkedin size={12} /> All articles on LinkedIn
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-surface-2 border-y border-surface-2">
          {themes.map((t, i) => (
            <motion.a
              key={t.title}
              href={NEWSLETTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`perspective-${i}`}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={row}
              className="placard bg-ink p-10 md:p-12 min-h-[300px] flex flex-col justify-between group border border-transparent"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="text-[10px] uppercase tracking-[0.28em] text-brass tabular">
                  0{i + 1} · {t.tag}
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-mutedfg group-hover:text-brass transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </div>
              <div>
                <h3 className="font-serif-display text-ivory text-2xl md:text-3xl font-light leading-[1.1] transition-colors duration-500 group-hover:text-brass">
                  {t.title}
                </h3>
                <p className="mt-5 text-mutedfg leading-relaxed font-light">{t.body}</p>
              </div>
              <div className="mt-8 text-[10px] uppercase tracking-[0.28em] text-mutedfg">
                Read perspective →
              </div>
            </motion.a>
          ))}
        </div>

        <p className="mt-10 text-mutedfg text-xs md:text-sm max-w-2xl leading-relaxed">
          New editions are published on LinkedIn. Follow the newsletter to be notified when articles go live.
        </p>
      </div>
    </section>
  );
}
